import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Chip
} from '@mui/material';
import { Security, Save, Refresh } from '@mui/icons-material';
const PermissionsManagement = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [hasChanges, setHasChanges] = useState(false);

  const roles = [
    { id: 2, name: 'Vétérinaire', color: 'error' },
    { id: 3, name: 'Secrétaire', color: 'primary' },
    { id: 4, name: 'Technicien', color: 'success' }
  ];

  const permissionCategories = {
    'Clients': ['Créer client', 'Modifier client', 'Supprimer client', 'Créer client secrétaire', 'Modifier client secrétaire', 'Vérifier doublon client', 'Créer client terrain'],
    'Animaux': ['Créer animal', 'Modifier animal', 'Supprimer animal', 'Créer animal secrétaire', 'Modifier animal secrétaire', 'Créer animal terrain'],
    'Consultations': ['Créer consultation', 'Modifier consultation', 'Voir historique médical', 'Voir historique secrétaire', 'Créer consultation terrain'],
    'Ordonnances & Prescriptions': ['Créer ordonnance', 'Créer prescription', 'Enregistrer acte médical', 'Consommer médicament'],
    'Stocks': ['Gérer stock principal', 'Voir alertes stock', 'Gérer stock principal secrétaire', 'Gérer stock détail', 'Voir stock technicien', 'Demander mouvement stock'],
    'Finances': ['Créer dette', 'Modifier dette', 'Créer paiement', 'Gérer dettes', 'Créer note de frais', 'Créer note de frais secrétaire', 'Créer note de frais technicien'],
    'Administration': ['Gérer utilisateurs cabinet', 'Créer utilisateur', 'Modifier utilisateur', 'Supprimer utilisateur']
  };

  useEffect(() => {
    loadPermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);

      // Simuler le chargement des permissions (à adapter selon votre API)
      // Pour l'instant, on initialise avec des données statiques
      const allPerms = Object.values(permissionCategories).flat();
      setPermissions(allPerms);

      // Initialiser les permissions par rôle (exemple)
      const initRolePerms = {
        2: allPerms.slice(0, 20), // Vétérinaire a presque tout
        3: allPerms.filter(p => p.includes('secrétaire') || p.includes('dette') || p.includes('stock')),
        4: allPerms.filter(p => p.includes('terrain') || p.includes('technicien'))
      };
      setRolePermissions(initRolePerms);
    } catch (error) {
      showSnackbar('Erreur lors du chargement', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (roleId, permission) => {
    setRolePermissions(prev => {
      const rolePerms = prev[roleId] || [];
      const newPerms = rolePerms.includes(permission)
        ? rolePerms.filter(p => p !== permission)
        : [...rolePerms, permission];
      
      return { ...prev, [roleId]: newPerms };
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Ici, appeler votre API pour sauvegarder les permissions
      // await axios.put(`${API_URL}/super-admin/permissions`, rolePermissions, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });
      
      showSnackbar('Permissions sauvegardées avec succès', 'success');
      setHasChanges(false);
    } catch (error) {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleReset = () => {
    if (window.confirm('Réinitialiser les modifications non sauvegardées ?')) {
      loadPermissions();
      setHasChanges(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  const currentRole = roles[currentTab];
  const currentRolePermissions = rolePermissions[currentRole.id] || [];

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" gutterBottom>
            <Security sx={{ mr: 1, verticalAlign: 'middle' }} />
            Gestion des Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Définir les accès et permissions pour chaque rôle
          </Typography>
        </div>
        <Box>
          {hasChanges && (
            <Chip label="Modifications non sauvegardées" color="warning" sx={{ mr: 2 }} />
          )}
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleReset}
            disabled={!hasChanges}
            sx={{ mr: 1 }}
          >
            Réinitialiser
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Sauvegarder
          </Button>
        </Box>
      </Box>

      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Vous avez des modifications non sauvegardées. Pensez à cliquer sur "Sauvegarder".
        </Alert>
      )}

      {/* Onglets des rôles */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}>
          {roles.map((role, index) => (
            <Tab
              key={role.id}
              label={
                <Box display="flex" alignItems="center">
                  {role.name}
                  <Chip
                    label={currentRolePermissions.length}
                    size="small"
                    color={role.color}
                    sx={{ ml: 1 }}
                  />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {/* Matrice des permissions */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell width="30%"><strong>Catégorie</strong></TableCell>
              <TableCell><strong>Permission</strong></TableCell>
              <TableCell align="center" width="100">
                <strong>Accès</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(permissionCategories).map(([category, perms]) => (
              <React.Fragment key={category}>
                {perms.map((permission, index) => (
                  <TableRow key={permission} hover>
                    {index === 0 && (
                      <TableCell rowSpan={perms.length} sx={{ fontWeight: 'bold', verticalAlign: 'top', pt: 2 }}>
                        {category}
                      </TableCell>
                    )}
                    <TableCell>{permission}</TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={currentRolePermissions.includes(permission)}
                        onChange={() => handlePermissionToggle(currentRole.id, permission)}
                        color={currentRole.color}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box mt={2}>
        <Typography variant="body2" color="text.secondary">
          Total permissions pour {currentRole.name} : {currentRolePermissions.length} / {permissions.length}
        </Typography>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PermissionsManagement;